import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { XIcon } from '@primer/octicons-react';
import { loadDetailHar, loadDetailLog, setDetailDrawer, setDetailLogsSub } from '../../../data/pack-store';
import { SegmentedControl } from '../SegmentedControl';
import { ConsoleLogViewer } from './ConsoleLogViewer';
import { NetworkHarPanel } from './NetworkHarPanel';
import { RawLogViewer } from './RawLogViewer';
import styles from './dock.module.css';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
var DEFAULT_SIZE = {
  right: 500,
  bottom: 320
};
var MIN_SIZE = 260;
var MAX_FRACTION = 0.7;
var sizeKey = function sizeKey(pos) {
  return "ev-detail-dock_".concat(pos);
};
var readStoredSize = function readStoredSize(pos) {
  try {
    var v = Number(window.localStorage.getItem(sizeKey(pos)));
    return Number.isFinite(v) && v >= MIN_SIZE ? v : DEFAULT_SIZE[pos];
  } catch (_unused) {
    return DEFAULT_SIZE[pos];
  }
};
var storeSize = function storeSize(pos, size) {
  try {
    window.localStorage.setItem(sizeKey(pos), String(Math.round(size)));
  } catch (_unused2) {
    // Storage unavailable — size is per-session.
  }
};

/** Dock-position glyphs (panel on the right / panel at the bottom). */
var DockRightIcon = function DockRightIcon() {
  return /*#__PURE__*/_jsxs("svg", {
    width: "16",
    height: "16",
    viewBox: "0 0 16 16",
    fill: "none",
    "aria-hidden": "true",
    children: [/*#__PURE__*/_jsx("rect", {
      x: "1.5",
      y: "2.5",
      width: "13",
      height: "11",
      rx: "1.5",
      stroke: "currentColor",
      strokeWidth: "1.25"
    }), /*#__PURE__*/_jsx("path", {
      d: "M10 2.5V13.5",
      stroke: "currentColor",
      strokeWidth: "1.25"
    })]
  });
};
var DockBottomIcon = function DockBottomIcon() {
  return /*#__PURE__*/_jsxs("svg", {
    width: "16",
    height: "16",
    viewBox: "0 0 16 16",
    fill: "none",
    "aria-hidden": "true",
    children: [/*#__PURE__*/_jsx("rect", {
      x: "1.5",
      y: "2.5",
      width: "13",
      height: "11",
      rx: "1.5",
      stroke: "currentColor",
      strokeWidth: "1.25"
    }), /*#__PURE__*/_jsx("path", {
      d: "M1.5 9.5H14.5",
      stroke: "currentColor",
      strokeWidth: "1.25"
    })]
  });
};
var TABS = [{
  key: 'logs',
  label: 'Logs'
}, {
  key: 'network',
  label: 'Network'
}];

/**
 * Port of kaneai's DevToolsDock + the Logs/Network subset of its dock
 * content: dockable right/bottom, drag-resizable (size persisted per
 * position), title bar with position toggle + close. Logs tab carries the
 * Console | Terminal sub-switch (kaneai's selectedTabSubType); Network is
 * an honest empty state until packs carry network data.
 */
export function LogsDock(_ref) {
  var detail = _ref.detail,
    position = _ref.position,
    onPositionChange = _ref.onPositionChange;
  var _useState = useState(function () {
      return DEFAULT_SIZE[position];
    }),
    _useState2 = _slicedToArray(_useState, 2),
    size = _useState2[0],
    setSize = _useState2[1];
  var dragRef = useRef(null);
  var tab = detail.drawer.tab;
  // Store-held so the command palette can deep-link a specific stream.
  var logsSub = detail.drawer.logsSub;
  useEffect(function () {
    setSize(readStoredSize(position));
  }, [position]);

  // Lazy loads: console fires via setDetailDrawer; terminal on first
  // switch; the HAR when the Network tab first opens.
  useEffect(function () {
    if (tab === 'logs') loadDetailLog(logsSub);else if (tab === 'network') loadDetailHar();
  }, [tab, logsSub]);
  var clampSize = useCallback(function (s) {
    var limit = position === 'right' ? window.innerWidth * MAX_FRACTION : window.innerHeight * MAX_FRACTION;
    return Math.min(Math.max(MIN_SIZE, s), Math.max(MIN_SIZE, limit));
  }, [position]);
  var onHandlePointerDown = function onHandlePointerDown(event) {
    var start = position === 'right' ? event.clientX : event.clientY;
    dragRef.current = {
      start: start,
      startSize: size
    };
    var onMove = function onMove(e) {
      var drag = dragRef.current;
      if (!drag) return;
      var delta = position === 'right' ? drag.start - e.clientX : drag.start - e.clientY;
      setSize(clampSize(drag.startSize + delta));
    };
    var _onUp = function onUp() {
      dragRef.current = null;
      setSize(function (s) {
        storeSize(position, s);
        return s;
      });
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', _onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', _onUp);
  };
  var onHandleKeyDown = function onHandleKeyDown(event) {
    var grow = position === 'right' ? 'ArrowLeft' : 'ArrowUp';
    var shrink = position === 'right' ? 'ArrowRight' : 'ArrowDown';
    if (event.key !== grow && event.key !== shrink) return;
    event.preventDefault();
    var delta = event.key === grow ? 24 : -24;
    setSize(function (s) {
      var next = clampSize(s + delta);
      storeSize(position, next);
      return next;
    });
  };
  return /*#__PURE__*/_jsxs("div", {
    className: "".concat(styles.dock, " ").concat(position === 'right' ? styles.dockRight : styles.dockBottom),
    style: position === 'right' ? {
      width: size
    } : {
      height: size
    },
    role: "complementary",
    "aria-label": "Test execution logs",
    children: [/*#__PURE__*/_jsx("div", {
      className: "".concat(styles.handle, " ").concat(position === 'right' ? styles.handleLeft : styles.handleTop),
      role: "separator",
      "aria-orientation": position === 'right' ? 'vertical' : 'horizontal',
      "aria-label": "Resize logs panel",
      "aria-valuenow": Math.round(size),
      tabIndex: 0,
      onPointerDown: onHandlePointerDown,
      onKeyDown: onHandleKeyDown
    }), /*#__PURE__*/_jsxs("div", {
      className: styles.titleBar,
      children: [/*#__PURE__*/_jsx("span", {
        className: styles.title,
        children: "Test Execution Logs"
      }), /*#__PURE__*/_jsxs("div", {
        className: styles.titleActions,
        children: [/*#__PURE__*/_jsx("button", {
          type: "button",
          className: styles.titleButton,
          "aria-label": position === 'right' ? 'Dock to bottom' : 'Dock to right',
          onClick: function onClick() {
            return onPositionChange(position === 'right' ? 'bottom' : 'right');
          },
          children: position === 'right' ? /*#__PURE__*/_jsx(DockBottomIcon, {}) : /*#__PURE__*/_jsx(DockRightIcon, {})
        }), /*#__PURE__*/_jsx("button", {
          type: "button",
          className: styles.titleButton,
          "aria-label": "Close logs panel",
          onClick: function onClick() {
            return setDetailDrawer(false);
          },
          children: /*#__PURE__*/_jsx(XIcon, {
            size: 16,
            "aria-hidden": "true"
          })
        })]
      })]
    }), /*#__PURE__*/_jsx("div", {
      className: styles.tabBar,
      role: "tablist",
      "aria-label": "Log views",
      children: TABS.map(function (t) {
        return /*#__PURE__*/_jsx("button", {
          type: "button",
          role: "tab",
          "aria-selected": tab === t.key,
          className: "".concat(styles.tab, " ").concat(tab === t.key ? styles.tabActive : ''),
          onClick: function onClick() {
            return setDetailDrawer(true, t.key);
          },
          children: t.label
        }, t.key);
      })
    }), tab === 'logs' ? /*#__PURE__*/_jsxs("div", {
      className: styles.body,
      children: [/*#__PURE__*/_jsx("div", {
        className: styles.subBar,
        children: /*#__PURE__*/_jsx(SegmentedControl, {
          ariaLabel: "Log type",
          value: logsSub,
          onChange: function onChange(v) {
            return setDetailLogsSub(v);
          },
          options: [{
            value: 'console',
            label: 'Console'
          }, {
            value: 'terminal',
            label: 'Terminal'
          }]
        })
      }), logsSub === 'console' ? /*#__PURE__*/_jsx(ConsoleLogViewer, {
        log: detail.console
      }) : /*#__PURE__*/_jsx(RawLogViewer, {
        log: detail.terminal,
        emptyText: "No terminal logs were recorded for this test."
      })]
    }) : /*#__PURE__*/_jsx("div", {
      className: styles.body,
      children: /*#__PURE__*/_jsx(NetworkHarPanel, {
        detail: detail
      })
    })]
  });
}